import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Motion, Pattern, Rank, type EnemyDef } from '../sim/defs';
import './bestiary.css';

/* ═══════════════════════════════════════════════════════════════════════════
   LE REGISTRE DES MENACES — l'encyclopédie des ennemis.

   C'est un écran de CONSULTATION, ouvert hors du jeu pour le plaisir de
   regarder ce qu'on a affronté. Il enrichit le rôle du journal de bord : le jeu
   n'affiche aucun texte explicatif pendant une partie (§6.12), et cet ailleurs
   est le seul endroit où le joueur peut comprendre pourquoi une figure le tue.

   ⚠️ RÈGLE DE RÉVÉLATION, non négociable.
   Un ennemi NON rencontré s'affiche VERROUILLÉ : une case « ??? », sans son nom
   ni ses statistiques. Révéler tout le bestiaire d'emblée divulguerait les trois
   mondes et les boss, dont l'arrivée est mise en scène par cinq secondes de
   silence — le seul effet de mise en scène du jeu, qu'une liste annulerait.

   On montre malgré tout la SILHOUETTE de toutes les cases (la liste complète,
   groupée par rang) : voir combien d'appareils restent à découvrir donne envie
   de jouer, sans rien divulguer de ce qu'ils sont. Le contenu ne se débloque
   qu'en les croisant au combat — c'est `rencontres` qui en décide, jamais cet
   écran.

   Monté HORS jeu uniquement : il ne s'abonne à rien et ne lit aucun état qui
   change à 60 Hz (PLAN.md §3.7).
   ═══════════════════════════════════════════════════════════════════════════ */

/* Traductions des formes du moteur en clair. Elles vivent ici, côté vue, et non
   dans le contenu : le moteur exécute des `kind` numériques, c'est la
   présentation qui les nomme pour un lecteur humain. */
const MOUVEMENTS: Readonly<Record<number, string>> = {
  [Motion.Linear]: 'descend tout droit',
  [Motion.Sine]: 'descend en ondulant',
  [Motion.Hover]: 'se poste et oscille',
  [Motion.Strafe]: 'traverse latéralement',
  [Motion.Dive]: 'attend, puis plonge',
  [Motion.Anchor]: 'se pose et ne bouge plus',
};

const TIRS: Readonly<Record<number, string>> = {
  [Pattern.None]: 'ne tire pas',
  [Pattern.Aimed]: 'visé sur le joueur',
  [Pattern.Fan]: 'éventail fixe',
  [Pattern.Ring]: 'anneau complet',
  [Pattern.Wall]: 'mur à brèche mobile',
  [Pattern.Homing]: 'missile guidé',
  [Pattern.Spiral]: 'spirale',
  [Pattern.Beam]: 'laser persistant',
};

const RANGS: Readonly<Record<number, string>> = {
  [Rank.Popcorn]: 'Piétaille',
  [Rank.Standard]: 'Troupe',
  [Rank.Heavy]: 'Lourd',
};

/** Seuil de PV au-delà duquel un ennemi de rang Lourd est présenté comme un
 *  BOSS plutôt que comme une unité de troupe.
 *
 *  Le rang seul ne suffit pas à les distinguer : les boss, mais aussi leurs
 *  parties destructibles (canons, bras, tourelles) et les élites de terrain
 *  (Bastion, Égide) partagent tous `Rank.Heavy`. Les boss de monde pèsent
 *  pourtant ≥ 18 000 PV, quand la plus grosse unité non-boss plafonne autour de
 *  1 500. Ce seuil tombe très largement entre les deux, sans jamais frôler ni
 *  l'un ni l'autre — il classe donc sans risque d'erreur, et sans que la vue
 *  ait à connaître un identifiant d'ennemi en particulier. */
const SEUIL_PV_BOSS = 5000;

function estBoss(e: EnemyDef): boolean {
  return e.rank === Rank.Heavy && e.hp >= SEUIL_PV_BOSS;
}

/** Nom d'affichage dérivé de l'identifiant, de façon déterministe. On ne montre
 *  ce nom QUE pour un ennemi rencontré — jamais pour une case verrouillée. */
function nomAffiche(id: string): string {
  return id.replace(/-/g, ' ').toUpperCase();
}

function libelleRang(e: EnemyDef): string {
  if (estBoss(e)) return 'Boss';
  return RANGS[e.rank] ?? '—';
}

interface Categorie {
  readonly cle: string;
  readonly titre: string;
  readonly membres: readonly EnemyDef[];
}

/** Regroupe par rang, l'ordre d'apparition d'un rang à l'écran suivant sa
 *  dangerosité croissante. L'ordre à l'intérieur d'un groupe est celui du
 *  catalogue reçu — le même que celui des identifiants numériques du moteur. */
function grouper(enemies: readonly EnemyDef[]): Categorie[] {
  const popcorn: EnemyDef[] = [];
  const troupe: EnemyDef[] = [];
  const lourd: EnemyDef[] = [];
  const boss: EnemyDef[] = [];
  for (const e of enemies) {
    if (estBoss(e)) boss.push(e);
    else if (e.rank === Rank.Popcorn) popcorn.push(e);
    else if (e.rank === Rank.Standard) troupe.push(e);
    else lourd.push(e);
  }
  return [
    { cle: 'popcorn', titre: 'PIÉTAILLE', membres: popcorn },
    { cle: 'troupe', titre: 'TROUPE', membres: troupe },
    { cle: 'lourd', titre: 'UNITÉS LOURDES', membres: lourd },
    { cle: 'boss', titre: 'BOSS', membres: boss },
  ].filter((c) => c.membres.length > 0);
}

/** La fiche détaillée d'un ennemi RENCONTRÉ. N'est jamais appelée pour une case
 *  verrouillée : c'est le seul verrou qui protège la règle de révélation. */
function renderFiche(e: EnemyDef): ReactElement {
  const boss = estBoss(e);
  const em = e.emitter;
  const tire = em.pattern !== Pattern.None;
  return (
    <div className={`best-fiche${boss ? ' boss' : ''}`}>
      <div className="best-fiche-entete">
        <span className="best-fiche-titre">{nomAffiche(e.id)}</span>
        <span className={`best-badge${boss ? ' boss' : ''}`}>{libelleRang(e)}</span>
      </div>

      <dl className="stats best-stats">
        <dt>Points de vie</dt>
        <dd>{e.hp.toLocaleString('fr-FR')}</dd>

        {/* Le bouclier frontal s'ajoute aux PV du corps : le montrer évite au
            joueur de conclure que ses tirs « ne font rien » alors qu'ils sont
            absorbés par le cône. */}
        {e.shieldHp !== undefined && (
          <>
            <dt>Bouclier frontal</dt>
            <dd>
              {e.shieldHp.toLocaleString('fr-FR')} PV
              {e.shieldConeDeg !== undefined ? ` · cône ±${e.shieldConeDeg}°` : ''}
            </dd>
          </>
        )}

        <dt>Déplacement</dt>
        <dd>{MOUVEMENTS[e.motion.kind] ?? '—'}</dd>
        <dt>Vitesse</dt>
        <dd>{e.motion.speed} u/s</dd>

        <dt>Armement</dt>
        <dd>{TIRS[em.pattern] ?? '—'}</dd>

        {/* Les détails de tir n'ont de sens que si l'ennemi tire. La piétaille
            sans émetteur n'affiche donc rien de tout cela. */}
        {tire && (
          <>
            <dt>Projectiles / volée</dt>
            <dd>{em.count}</dd>
            <dt>Cadence</dt>
            <dd>toutes les {em.every.toFixed(1)} s</dd>
            <dt>Vitesse des tirs</dt>
            <dd>{em.speed} u/s</dd>
            {em.turnDeg !== undefined && (
              <>
                <dt>Virage</dt>
                <dd>{em.turnDeg}°/s</dd>
              </>
            )}
          </>
        )}

        <dt>Télégraphe</dt>
        <dd>{e.telegraph.toFixed(1)} s</dd>

        {e.parts !== undefined && e.parts.length > 0 && (
          <>
            <dt>Parties destructibles</dt>
            <dd>
              {e.parts.length}
              {e.shieldedByParts ? ' · noyau scellé' : ''}
            </dd>
          </>
        )}

        <dt>Prime</dt>
        <dd>{e.score.toLocaleString('fr-FR')} pts</dd>
      </dl>
    </div>
  );
}

export function BestiaryScreen({
  enemies,
  rencontres,
  onBack,
}: {
  /** Le catalogue COMPLET des ennemis — verrouillés compris, pour la silhouette. */
  enemies: readonly EnemyDef[];
  /** Identifiants déjà croisés en jeu. La seule clé de révélation. */
  rencontres: ReadonlySet<string>;
  onBack: () => void;
}): ReactElement {
  const total = enemies.length;
  const connus = enemies.filter((e) => rencontres.has(e.id));
  const groupes = grouper(enemies);

  // Sélection par défaut : le premier ennemi RENCONTRÉ, pour ouvrir sur une
  // fiche pleine plutôt que sur une case verrouillée. À défaut (rien de
  // rencontré), on pointe la première case du catalogue : la fiche affichera
  // alors le message de verrouillage, ce qui est exactement l'invitation
  // recherchée.
  const [selection, setSelection] = useState<string>(connus[0]?.id ?? enemies[0]?.id ?? '');
  const courant = enemies.find((e) => e.id === selection);

  // Focus initial sur la case sélectionnée (ou, à défaut, le bouton RETOUR).
  // Sans focus de départ, la navigation clavier et manette ne démarre pas —
  // même exigence que sur les autres écrans (voir screens.tsx).
  const focusRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    (focusRef.current ?? backRef.current)?.focus();
  }, []);

  return (
    <div className="screen bestiary">
      <h2>REGISTRE DES MENACES</h2>
      <p className="best-compteur">
        <span className="best-compteur-n">{connus.length}</span> / {total} répertoriés
      </p>

      {total === 0 ? (
        <p>Aucun appareil au catalogue.</p>
      ) : (
        <div className="best-cols">
          <div className="best-grille">
            {groupes.map((g) => (
              <section key={g.cle} className="best-groupe">
                <h3>{g.titre}</h3>
                <div className="best-tuiles">
                  {g.membres.map((e) => {
                    const vu = rencontres.has(e.id);
                    const actif = e.id === selection;
                    const cls = [
                      'best-tuile',
                      vu ? 'vu' : 'verrou',
                      estBoss(e) ? 'boss' : '',
                      actif ? 'actif' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <button
                        // `key` n'est jamais rendu dans le DOM : utiliser l'id
                        // ici ne divulgue rien d'une case verrouillée.
                        key={e.id}
                        ref={actif ? focusRef : undefined}
                        className={cls}
                        // ⚠️ Aucun libellé identifiant sur une case verrouillée :
                        // ni texte, ni aria-label, ni attribut de données.
                        aria-label={vu ? nomAffiche(e.id) : 'Appareil non répertorié'}
                        aria-pressed={actif}
                        onClick={() => setSelection(e.id)}
                      >
                        {vu ? nomAffiche(e.id) : '???'}
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <div className="best-fiche-col">
            {/* Le `courant &&` narrow le type pour `renderFiche`, et surtout il
                garantit qu'une case verrouillée n'atteint JAMAIS la fiche
                détaillée : elle retombe toujours sur le message masqué. */}
            {courant && rencontres.has(courant.id) ? (
              renderFiche(courant)
            ) : (
              <div className="best-fiche verrou">
                <div className="best-fiche-titre">???</div>
                <p className="best-verrou-msg">
                  Appareil non répertorié. Ses caractéristiques se débloquent en le croisant au
                  combat — le registre ne divulgue rien que tu n'aies déjà vu.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <button ref={backRef} className="btn btn-primary" onClick={onBack}>
        RETOUR
      </button>
    </div>
  );
}
